<script setup lang="ts">
import { computed } from 'vue'

import type { RichTextViewerProps } from './types'

import { sanitizeHtml } from '@/shared/ui/sanitize'

// Read-only render for rich text. REBUILD of OrphexRichTextViewer.vue, whose `v-html="content"`
// had NO sanitisation (analysis §2.5 defect #1 — the XSS hole). This viewer routes EVERY string
// through the shared dompurify boundary, which is the prerequisite for the ledger §C Orpheus fix.
const props = withDefaults(defineProps<RichTextViewerProps>(), { content: '' })

const safeHtml = computed(() => sanitizeHtml(props.content))
</script>

<template>
  <!-- eslint-disable-next-line vue/no-v-html -- content is sanitised via the shared boundary above -->
  <div class="ui-rte-viewer break-words font-sans text-fg" v-html="safeHtml" />
</template>

<style scoped>
.ui-rte-viewer > *:first-child {
  margin-top: 0;
}
.ui-rte-viewer :deep(a) {
  font-weight: 500;
  color: var(--ui-color-info);
  text-decoration-line: underline;
}
.ui-rte-viewer :deep(blockquote) {
  margin-block: 0.5em;
  border-inline-start-style: solid;
  border-inline-start-width: 3px;
  border-color: var(--ui-color-border);
  padding-inline-start: var(--ui-space-md);
  font-style: italic;
  color: var(--ui-color-muted);
}
.ui-rte-viewer :deep(code) {
  border-radius: var(--ui-radius-sm);
  background-color: var(--ui-color-surface-sunken);
  padding-inline: 0.3rem;
  padding-block: 0.1rem;
  font-family: var(--ui-font-numeric);
  font-size: 0.9em;
}
.ui-rte-viewer :deep(h1) {
  margin-bottom: 0.3em;
  margin-top: 0.6em;
  font-size: 1.4em;
  font-weight: 700;
  line-height: 1.25;
  color: var(--ui-color-fg);
}
.ui-rte-viewer :deep(h2) {
  margin-bottom: 0.3em;
  margin-top: 0.5em;
  font-size: 1.2em;
  font-weight: 600;
  line-height: 1.3;
  color: var(--ui-color-fg);
}
.ui-rte-viewer :deep(hr) {
  margin-block: 1em;
  border-bottom-style: solid;
  border-bottom-width: 0px;
  border-top-style: solid;
  border-top-width: 1px;
  border-color: var(--ui-color-border);
}
.ui-rte-viewer :deep(ol) {
  margin-block: 0.25em;
  list-style-type: decimal;
  padding-inline-start: 1.2em;
}
.ui-rte-viewer :deep(p) {
  margin-block: 0.25em;
  line-height: 1.5;
}
.ui-rte-viewer :deep(ul) {
  margin-block: 0.25em;
  list-style-type: disc;
  padding-inline-start: 1.2em;
}
</style>
